import { Alert, Box, Button, Skeleton, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { neutral, palette } from '@ahora-patagonia/design-tokens';
import { EspacioPublicitario } from '../../../shared/ads/EspacioPublicitario.js';
import { TiraLateral } from '../../../shared/lateral/TiraLateral.js';
import { ListadoNotas } from '../../../shared/notas/ListadoNotas.js';
import { useObservable } from '../../../shared/hooks/useObservable.js';
import { useViewModel } from '../../../shared/hooks/useViewModel.js';
import { useSincronizarPagina } from '../../../shared/shell/paginaPublica.js';
import { useSenalDePagina } from '../../../shared/senales/useSenalDePagina.js';
import { useHead } from '../../../shared/seo/useHead.js';
import { useSitio, useSsrData } from '../../../shared/ssr/SsrDataProvider.js';
import { headDeSeccion } from '../../seccion.head.js';
import { SECCION_KIND, type SeccionData, type SeccionKind } from '../../models/entities/SeccionData.js';
import { SeccionViewModel } from '../../viewmodels/SeccionViewModel.js';

export interface SeccionViewProps {
  kind: SeccionKind;
  routeKey: string;
  /** Qué decir cuando no hay nada. Es distinto en cada una, y es la mitad del sentido de la página. */
  vacio: string;
}

/**
 * `/ultima-hora` y `/mas-leidas`: la misma página que una sección —cabecera con título y bajada, y un
 * listado uniforme de 12 con "Ver más"— pero armada desde un bloque en vez de una categoría.
 *
 * Existen porque las dos ahora pueden estar en la cabecera, y un link del menú necesita a dónde ir.
 *
 * Un componente para las dos: lo único que cambia es de dónde salen los datos y qué dice el estado
 * vacío. Duplicar la pantalla sería duplicar el layout, el lateral, el paginado y los avisos.
 */
export function SeccionView({ kind, routeKey, vacio }: SeccionViewProps) {
  const sitio = useSitio();
  const initial = useSsrData<SeccionData>(routeKey);
  const vm = useViewModel(() => new SeccionViewModel(initial, kind));
  const state = useObservable(vm.state$);
  const esUltimaHora = kind === SECCION_KIND.ultimaHora;

  // Depende de `kind`: `/ultima-hora` y `/mas-leidas` son dos rutas pero un solo componente, así
  // que saltar de una a otra **no** remonta la vista y un efecto con `[vm]` no se volvía a disparar.
  useEffect(() => {
    void vm.irA(kind);
  }, [vm, kind]);
  useSincronizarPagina(state.data);
  // Última hora y Más leídas no son categorías: cuentan como sección, sin entidad.
  useSenalDePagina('SECCION', state.data ? kind : null);
  useHead(
    useMemo(
      () => (state.data ? headDeSeccion(kind, state.data, sitio) : null),
      [state.data, sitio, kind],
    ),
  );

  const data = state.data;
  if (!data) {
    return (
      <Box sx={{ py: 2 }}>
        {state.error ? (
          <Alert severity="error">{state.error}</Alert>
        ) : (
          <>
            <Skeleton height={48} width="40%" />
            <Skeleton variant="rectangular" height={96} sx={{ mt: 2 }} />
            <Skeleton variant="rectangular" height={96} sx={{ mt: 1 }} />
          </>
        )}
      </Box>
    );
  }

  return (
    <>
      <EspacioPublicitario ubicacion="HEADER" ad={data.ads.HEADER} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 300px' },
          gap: { xs: 0, lg: 4 },
          alignItems: 'stretch',
          mt: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ borderBottom: `2px solid ${palette.ink}`, pb: 1 }}>
            <Typography variant="tituloNota" component="h1">
              {data.titulo}
            </Typography>
            <Typography variant="bajada" component="p" sx={{ color: neutral[700], mt: 0.5 }}>
              {data.descripcion}
            </Typography>
          </Box>

          {state.items.length === 0 ? (
            <Typography variant="bajada" component="p" sx={{ color: neutral[700], py: 4 }}>
              {vacio}
            </Typography>
          ) : (
            <>
              {/* Sin numerar, a diferencia del bloque de la portada: acá el listado es la página
                  entera y las posiciones 13 y 14 de un ranking no le dicen nada a nadie. */}
              <ListadoNotas notas={state.items} />
              {state.items.length < data.total && (
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={state.cargandoMas}
                  onClick={() => void vm.verMas()}
                  sx={{ mt: 2 }}
                >
                  {state.cargandoMas ? 'Cargando…' : 'Ver más'}
                </Button>
              )}
            </>
          )}
        </Box>

        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
          {/* En `/ultima-hora` la tira de Última hora repetiría el cuerpo fila por fila, así que
              ahí va el ranking —numerado, y con su propio "Ver todo"—. En `/mas-leidas` es al
              revés. El servidor manda poblada solo la que corresponde; la otra llega vacía. */}
          <TiraLateral
            notas={esUltimaHora ? data.masLeidas : data.ultimaHora}
            titulo={esUltimaHora ? data.shell.masLeidasNombre : data.shell.ultimaHoraNombre}
            verTodo={`/${esUltimaHora ? SECCION_KIND.masLeidas : SECCION_KIND.ultimaHora}`}
            numerada={esUltimaHora}
            ad={data.ads.SIDEBAR}
          />
        </Box>
      </Box>
    </>
  );
}

/** El estado vacío de cada una: dice qué falta y a dónde ir, no "no hay resultados". */
export const SECCION_VACIA = {
  [SECCION_KIND.ultimaHora]:
    'Todavía no hay notas publicadas. Cuando la redacción publique la primera, va a aparecer acá.',
  // La página **no rellena** con las últimas publicadas (a diferencia de la franja de la portada), así
  // que sin lecturas registradas queda vacía de verdad. El texto lo dice sin hablar de contadores.
  [SECCION_KIND.masLeidas]:
    'Todavía no hay lecturas suficientes para armar un ranking. Mirá lo último publicado en la portada.',
} as const;
